import React, { useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Fade,
  Popover,
} from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "../context/ThemeContext";

/**
 * Theme Picker Component
 *
 * Two variants:
 *  - "floating" (default): a fixed, corner-anchored button with a dropdown panel.
 *  - "inline": a plain icon button meant to sit inside a navbar/toolbar.
 *
 * @param {object} props
 * @param {string} props.position - Corner for the floating variant ("top-right", "top-left", "bottom-right", "bottom-left")
 * @param {boolean} props.light - Use light button style (for dark backgrounds)
 * @param {string} props.variant - "floating" | "inline"
 * @param {number} props.size - Button size in px (inline variant)
 */
const ThemePicker = ({
  position = "top-right",
  light = true,
  variant = "floating",
  size = 40,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [styleFilter, setStyleFilter] = useState("gradient");
  const { currentTheme, theme, themes, changeTheme } = useTheme();

  // Position styles (top positions account for navbar ~70px)
  const positionStyles = {
    "top-right": { top: 80, right: 20 },
    "top-left": { top: 80, left: 20 },
    "bottom-right": { bottom: 20, right: 20 },
    "bottom-left": { bottom: 20, left: 20 },
  };

  // Panel position based on button position
  const panelPosition = {
    "top-right": { top: 60, right: 0 },
    "top-left": { top: 60, left: 0 },
    "bottom-right": { bottom: 60, right: 0 },
    "bottom-left": { bottom: 60, left: 0 },
  };

  /* Gradient / Solid style switch shared by both variants */
  const StyleFilterTabs = () => (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        mb: 1.5,
        p: 0.5,
        borderRadius: 2.5,
        bgcolor: "rgba(0,0,0,0.04)",
      }}
    >
      {[
        { key: "gradient", label: "Gradient" },
        { key: "solid", label: "Solid" },
      ].map((opt) => (
        <Box
          key={opt.key}
          role="button"
          tabIndex={0}
          onClick={() => setStyleFilter(opt.key)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setStyleFilter(opt.key);
            }
          }}
          sx={{
            flex: 1,
            textAlign: "center",
            py: 0.5,
            borderRadius: 2,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: styleFilter === opt.key ? 600 : 400,
            color: styleFilter === opt.key ? "#222" : "text.secondary",
            bgcolor: styleFilter === opt.key ? "#fff" : "transparent",
            boxShadow: styleFilter === opt.key ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
            transition: "all 0.2s ease",
          }}
        >
          {opt.label}
        </Box>
      ))}
    </Box>
  );

  /* Swatch list shared by both variants */
  const ThemeList = ({ columns = 1, onPick }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 1,
      }}
    >
      {Object.entries(themes)
        .filter(([, value]) => (value.type || "gradient") === styleFilter)
        .map(([key, value]) => {
        const active = currentTheme === key;
        return (
          <Box
            key={key}
            role="button"
            tabIndex={0}
            aria-pressed={active}
            aria-label={`${value.name} theme`}
            onClick={() => {
              changeTheme(key);
              onPick?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                changeTheme(key);
                onPick?.();
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 1,
              borderRadius: 1.5,
              cursor: "pointer",
              bgcolor: active ? value.lightBg : "transparent",
              border: "2px solid",
              borderColor: active ? value.primary : "transparent",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: value.lightBg,
                transform: "translateY(-1px)",
              },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background:
                  value.type === "solid"
                    ? value.primary
                    : `linear-gradient(135deg, ${value.primary} 0%, ${value.secondary} 100%)`,
                boxShadow: active
                  ? `0 0 0 3px ${value.lightBg}, 0 0 0 5px ${value.primary}`
                  : "0 2px 6px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
              }}
            >
              {active && <CheckIcon sx={{ fontSize: 16 }} />}
            </Box>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: active ? 600 : 400,
                color: active ? value.primary : "#666",
              }}
            >
              {value.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  /* ---------------- Inline variant (navbar / toolbar) ---------------- */
  if (variant === "inline") {
    return (
      <>
        <Tooltip title="Change theme">
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              width: size,
              height: size,
              color: light ? "#fff" : theme.primary,
              bgcolor: light ? "rgba(255,255,255,0.15)" : `${theme.primary}14`,
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: light ? "rgba(255,255,255,0.28)" : `${theme.primary}26`,
                transform: "rotate(25deg)",
              },
            }}
          >
            <PaletteIcon sx={{ fontSize: size * 0.5 }} />
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              elevation: 8,
              sx: {
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                width: { xs: 260, sm: 320 },
                border: "1px solid rgba(0,0,0,0.06)",
              },
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, fontWeight: 700, color: "#222" }}
          >
            Pick your vibe
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1.5, color: "text.secondary" }}
          >
            Your choice is saved on this device.
          </Typography>
          <StyleFilterTabs />
          <ThemeList
            columns={2}
            onPick={() => setAnchorEl(null)}
          />
        </Popover>
      </>
    );
  }

  /* ---------------- Floating variant (default) ---------------- */
  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: 1200,
        ...positionStyles[position],
      }}
    >
      <Tooltip title="Change Theme" placement="left">
        <IconButton
          onClick={() => setShowPicker(!showPicker)}
          sx={{
            bgcolor: light ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)",
            backdropFilter: "blur(10px)",
            color: light ? "white" : theme.primary,
            width: 48,
            height: 48,
            transition: "all 0.3s ease",
            border: light ? "none" : `2px solid ${theme.primary}20`,
            "&:hover": {
              bgcolor: light ? "rgba(255,255,255,0.25)" : theme.lightBg,
              transform: "rotate(15deg)",
            },
          }}
        >
          {showPicker ? <CloseIcon /> : <PaletteIcon />}
        </IconButton>
      </Tooltip>

      {/* Theme Picker Panel */}
      <Fade in={showPicker}>
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            ...panelPosition[position],
            p: 2,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.98)",
            backdropFilter: "blur(20px)",
            minWidth: 200,
            maxHeight: "70vh",
            overflowY: "auto",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 600, color: "#333" }}
          >
            Select Theme
          </Typography>
          <StyleFilterTabs />
          <ThemeList columns={1} onPick={() => setShowPicker(false)} />
        </Paper>
      </Fade>
    </Box>
  );
};

export default ThemePicker;
